import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Save, Play, RefreshCw } from 'lucide-react';
import { useVoiceConfig, useVoiceAgents, useAgentProfiles } from '../../hooks/useVoiceAPI';
import apiService from '../../services/apiService';
import { useBranding } from '../../context/BrandingContext';

const VoiceConfigPanel = ({ profileId }) => {
    const brandingContext = useBranding();
    const isFitenko = Boolean(
        brandingContext?.isFitenko ||
        brandingContext?.branding?.instance_name === 'fitenko' ||
        (typeof window !== 'undefined' && (
            window.location.hostname.includes('fitenko') ||
            localStorage.getItem('tenant_id') === 'fitenko' ||
            localStorage.getItem('lastTenant') === 'fitenko'
        ))
    );

    const { config, loading, updateConfig, refetch: refetchConfig } = useVoiceConfig(profileId);
    const { agents } = useVoiceAgents();

    // [PROTOCOL v5.1] Multilingual Tab State
    const [activeLang, setActiveLang] = useState('en-US');
    const [saving, setSaving] = useState(false);

    // --- Синхронизация с Vapi (quick 260901-q2w) ---
    // Промпт, который слышит звонящий, живёт в Vapi. Эта страница до правки писала
    // во ВТОРУЮ, мёртвую копию и безусловно говорила «сохранено».
    const [pulling, setPulling] = useState(false);
    // 🔴 Отпечаток, который ВИДЕЛ БРАУЗЕР в момент загрузки текста в форму.
    // Он закрывает щель, которую сверка «база ↔ Vapi» не закрывает: вторая
    // открытая вкладка переживает чужой pull, хэши базы и Vapi сходятся,
    // и старый текст из неё уехал бы поверх живого.
    const [baseHash, setBaseHash] = useState(null);
    // Вердикт держится на странице до следующего действия — не alert, который
    // закрывается без следа.
    const [syncVerdict, setSyncVerdict] = useState(null);

    // [INIT] Default State to avoid uncontrolled inputs or early null access
    const [formData, setFormData] = useState({
        twilioPhoneNumber: '',
        provider: 'internal', // [FIX] Default to MLG Internal Engine
        sttProvider: 'deepgram',
        ttsProvider: 'deepgram',
        llmModel: 'gemini-2.5-flash',
        voiceProvider: 'elevenlabs',
        voiceId: '',
        systemPromptTemplate: '',
        firstMessage: '',
        isActive: true,
        voiceAgentId: null,
        deepgramModel: 'nova-3',
        enabledLanguages: { en: true, ru: true, es: true },
        guardrails: {
            enableTools: true,
            transferOnAngry: false,
            requireConsent: true,
            maxCallDuration: 600,
        }
    });

    // [FIX] Dependent on formData ID (Dropdown) not just Config (Database)
    // This ensures when user switches roles, we fetch the new role's profiles immediately.
    const agentId = formData.voiceAgentId || config?.voice_agent_id || null;
    const { profiles, refetch: refetchProfiles } = useAgentProfiles(agentId);

    // Профиль активного языка: коды на фронте — en-US / ru-RU / es-ES.
    const activeProfile = profiles.find(p => p.language_code === activeLang) || null;
    const isLinkedToVapi = Boolean(activeProfile && activeProfile.vapi_assistant_id);

    // Отпечаток запоминается ровно в тот момент, когда текст лёг в форму.
    useEffect(() => {
        setBaseHash((activeProfile && activeProfile.vapi_prompt_hash) || null);
    }, [activeProfile]);

    // Вердикт снимается при смене языка, но НЕ при перечитывании профилей после
    // сохранения — иначе баннер исчез бы ровно тогда, когда его надо прочитать.
    useEffect(() => {
        setSyncVerdict(null);
    }, [activeLang]);

    // 1. Load Initial Config (Routing & English Defaults)
    useEffect(() => {
        if (config) {
            setFormData(prev => {
                // Only update if we haven't touched the form yet or if config ID changed
                if (prev.voiceAgentId && prev.voiceAgentId !== config.voice_agent_id) return prev;

                return {
                    ...prev,
                    twilioPhoneNumber: config.twilio_phone_number || config.twilioPhoneNumber || '',
                    provider: config.provider || 'vapi',
                    sttProvider: config.stt_provider || config.sttProvider || 'deepgram',
                    ttsProvider: config.tts_provider || config.ttsProvider || 'deepgram',
                    llmModel: config.llm_model || config.llmModel || 'gemini-2.5-flash',
                    voiceProvider: config.voice_provider || config.voiceProvider || 'elevenlabs',
                    voiceId: config.voice_id || config.voiceId || '',
                    systemPromptTemplate: config.system_prompt_template || config.systemPromptTemplate || '',
                    firstMessage: config.first_message || config.firstMessage || '',
                    isActive: config.is_active !== undefined ? config.is_active : (config.isActive !== undefined ? config.isActive : true),
                    voiceAgentId: config.voice_agent_id || config.voiceAgentId || null,
                    guardrails: config.guardrails_config || config.guardrailsConfig || {
                        enableTools: true,
                        transferOnAngry: false,
                        requireConsent: true,
                        maxCallDuration: 600,
                    },
                    deepgramModel: config.deepgram_model || config.deepgramModel || 'nova-3',
                    enabledLanguages: config.enabled_languages || config.enabledLanguages || { en: true, ru: true, es: true },
                };
            });
        }
    }, [config]);

    // 2. [FIX] Auto-select Casey (FRONT_DESK) if no agent is selected yet
    useEffect(() => {
        if (!formData.voiceAgentId && agents.length > 0) {
            const casey = agents.find(a => a.internal_name === 'Casey' || a.role_type === 'FRONT_DESK');
            if (casey) {
                setFormData(prev => ({ ...prev, voiceAgentId: casey.id }));
            }
        }
    }, [agents, formData.voiceAgentId]);

    // 3. Загрузка промптов и голоса в зависимости от языка и выбранного агента
    useEffect(() => {
        if (!formData.voiceAgentId) return;

        if (activeLang === 'en-US') {
            // А. Пробуем загрузить профиль языка
            const enProfile = profiles.find(p => p.language_code === 'en-US');
            if (enProfile) {
                setFormData(prev => ({
                    ...prev,
                    systemPromptTemplate: enProfile.system_prompt || '',
                    firstMessage: enProfile.first_message || '',
                    voiceId: enProfile.voice_id || prev.voiceId,
                    voiceProvider: enProfile.voice_provider || prev.voiceProvider,
                }));
                return;
            }

            // Б. Fallback на самого агента (Legacy fields)
            if (agents.length > 0) {
                const agent = agents.find(a => a.id === formData.voiceAgentId);
                if (agent) {
                    setFormData(prev => ({
                        ...prev,
                        systemPromptTemplate: agent.system_prompt || '',
                        firstMessage: agent.first_message || prev.firstMessage,
                    }));
                }
            }
        } else {
            // Иностранные языки - только через профиль
            const profile = profiles.find(p => p.language_code === activeLang);
            if (profile) {
                setFormData(prev => ({
                    ...prev,
                    systemPromptTemplate: profile.system_prompt || '',
                    firstMessage: profile.first_message || '',
                    voiceId: profile.voice_id || '',
                    voiceProvider: profile.voice_provider || 'elevenlabs',
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    systemPromptTemplate: '',
                    firstMessage: '',
                    voiceId: '',
                    voiceProvider: 'elevenlabs',
                }));
            }
        }
    }, [activeLang, profiles, formData.voiceAgentId, agents]);

    // 🔴 Обновить текст из Vapi — ЕДИНСТВЕННЫЙ способ увидеть на странице то,
    // что реально исполняется на звонке.
    const handlePullFromVapi = async () => {
        if (!formData.voiceAgentId || !isLinkedToVapi) return;
        setPulling(true);
        setSyncVerdict(null);
        try {
            const res = await apiService.makeRequest(
                `/voice/agents/${formData.voiceAgentId}/profiles/${activeLang}/pull-from-vapi`,
                { method: 'POST' }
            );
            await refetchProfiles();
            setSyncVerdict({
                tone: 'ok',
                text: `Загружено из Vapi: ${res.chars} знаков (язык ${activeLang}). ` +
                      `Это текст, который исполняется на звонке.`,
            });
        } catch (error) {
            // 🔴 Причина живёт в error.message: apiService бросает new Error(detail),
            // у fetch нет error.response — error.response?.data?.detail всегда undefined.
            setSyncVerdict({
                tone: 'error',
                text: `Не удалось загрузить из Vapi: ${error.message}`,
            });
        } finally {
            setPulling(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSyncVerdict(null);

        try {
            // A. Save Profile (if agent selected)
            let vapiSync = null;
            if (formData.voiceAgentId && formData.voiceAgentId !== "custom") {
                const profilePayload = {
                    language_code: activeLang,
                    voice_id: formData.voiceId,
                    system_prompt: formData.systemPromptTemplate,
                    first_message: formData.firstMessage,
                    voice_provider: formData.voiceProvider,
                    tools_config: []
                };

                // 🔴 Отпечаток браузера уходит query-параметром. Если его нет
                // (профиль не привязан или ни разу не синхронизирован) — параметр
                // НЕ добавляется вовсе: ни пустым, ни строкой 'undefined'. Иначе он
                // встретится с проверкой на бэкенде и даст 409 там, где обещан skipped.
                const query = baseHash ? `?base_hash=${encodeURIComponent(baseHash)}` : '';

                const saveRes = await apiService.makeRequest(
                    `/voice/agents/${formData.voiceAgentId}/profiles/${activeLang}${query}`,
                    {
                        method: 'PUT',
                        body: JSON.stringify(profilePayload),
                    }
                );
                vapiSync = saveRes && saveRes.vapi_sync ? saveRes.vapi_sync : null;
            }

            // B. Save Config (Routing)
            const routingPayload = {
                profile_id: profileId === "default" ? null : profileId,
                twilio_phone_number: formData.twilioPhoneNumber,
                agent_role: agents.find(a => a.id === formData.voiceAgentId)?.role_type || "FRONT_DESK",
                voice_agent_id: formData.voiceAgentId === "custom" ? null : formData.voiceAgentId,
                default_language_code: "en-US",
                is_active: formData.isActive,
                deepgram_model: formData.deepgramModel || "nova-3",
                provider: formData.provider,  // ← Added!
                stt_provider: formData.sttProvider,  // ← Added!
                tts_provider: formData.ttsProvider,  // ← Added!
                llm_model: formData.llmModel,  // ← Added!
                voice_provider: formData.voiceProvider,  // ← Added!
                voice_id: formData.voiceId,  // ← Added!
                enabled_languages: formData.enabledLanguages,
            };

            await updateConfig(routingPayload);

            // 🔴 Вердикт не обещает больше, чем сделано.
            if (vapiSync && vapiSync.status === 'pushed') {
                const notPushed = (vapiSync.not_pushed_fields || []).join(', ');
                setSyncVerdict({
                    tone: 'ok',
                    text:
                        `В Vapi уехал СИСТЕМНЫЙ ПРОМПТ: ${vapiSync.chars} знаков (язык ${activeLang}). ` +
                        (notPushed
                            ? `Остальное (${notPushed}) сохранено только в базе OS и на звонки НЕ влияет — ` +
                              'эти поля меняются в панели Vapi. '
                            : '') +
                        '⚠️ Если у вас открыта вкладка панели Vapi — обновите её (⌘R). ' +
                        'Иначе «Publish» из неё вернёт текст, каким он был ДО этого сохранения: ' +
                        'панель держит собственный черновик, и запись через API идёт мимо него.',
                });
            } else if (vapiSync && vapiSync.status === 'skipped') {
                setSyncVerdict({
                    tone: 'warn',
                    text:
                        'Сохранено ТОЛЬКО в базе OS. В эфир НЕ уехало: этот язык не привязан ' +
                        'к ассистенту Vapi, поэтому правка на звонки не влияет.',
                });
            } else {
                setSyncVerdict({
                    tone: 'warn',
                    text: 'Настройки маршрутизации сохранены. Синхронизация с Vapi не выполнялась.',
                });
            }
            refetchProfiles();
            refetchConfig();

        } catch (error) {
            console.error(error);
            // 🔴 Причина приходит в error.message (apiService бросает new Error(detail)).
            const reason = error.message;
            if (reason && reason.indexOf('VAPI_PATCH_APPLIED_VERIFY_FAILED') !== -1) {
                // 🔴 ОТДЕЛЬНАЯ ветка. PATCH уже применён: промпт в Vapi ЗАМЕНЁН,
                // и сказать здесь «не уехало» — та же ложь измерителя, которую
                // чинит вся эта правка, и сказана она ровно тогда, когда человеку
                // решать про откат.
                setSyncVerdict({
                    tone: 'error',
                    text:
                        `🔴 Запись в Vapi ВЫПОЛНЕНА, но сверка после записи не сошлась: ${reason} ` +
                        'В базе OS не сохранено ничего. Откатывайте по снимку, путь указан выше.',
                });
            } else {
                setSyncVerdict({
                    tone: 'error',
                    text:
                        `НЕ уехало в Vapi: ${reason} ` +
                        'Не сохранено НИЧЕГО — ни в Vapi, ни в базе OS, ни настройки маршрутизации. ' +
                        'Текст остался в форме, не закрывайте вкладку.',
                });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleTestCall = async () => {
        alert('Test call initiated (Mock)');
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '16rem' }}>
                <div style={{ width: '2rem', height: '2rem', border: '3px solid #e5e7eb', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <Tabs defaultValue="basic" style={{ width: '100%' }}>
                <TabsList 
                    className="grid grid-cols-1 sm:grid-cols-3 gap-1 p-1 w-full"
                    style={{ 
                        height: 'auto', 
                        minHeight: '2.5rem',
                        backgroundColor: isFitenko ? '#F1EEE4' : '#f1f5f9',
                        border: isFitenko ? '1px solid #E5E1D6' : 'none',
                        borderRadius: '0.5rem'
                    }}
                >
                    <TabsTrigger value="basic" className="py-2 text-xs sm:text-sm font-medium">Basic Settings</TabsTrigger>
                    <TabsTrigger value="voice" className="py-2 text-xs sm:text-sm font-medium">Voice & Behavior</TabsTrigger>
                    <TabsTrigger value="guardrails" className="py-2 text-xs sm:text-sm font-medium">Guardrails</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <Card style={{ padding: '1.5rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                            {/* [PROTOCOL v5.1] AI Role Selector */}
                            <div style={{
                                padding: '1rem',
                                backgroundColor: isFitenko ? '#FAF4E5' : '#f0f9ff',
                                border: isFitenko ? '1px solid #E5E1D6' : '1px solid #bae6fd',
                                borderRadius: '0.5rem'
                            }}>
                                <Label htmlFor="voiceAgent" style={{ color: isFitenko ? '#20231F' : undefined, fontWeight: '600' }}>AI Role (Personality & Skills)</Label>
                                <Select
                                    value={formData.voiceAgentId || "custom"}
                                    onValueChange={(value) => {
                                        const newVal = value === "custom" ? null : value;
                                        setFormData({ ...formData, voiceAgentId: newVal });
                                    }}
                                >
                                    <SelectTrigger style={{ backgroundColor: 'white' }}>
                                        {/* [FIX] Manually render label because SelectValue displays the raw ID */}
                                        <span>
                                            {(() => {
                                                if (!formData.voiceAgentId || formData.voiceAgentId === "custom") return "Manual Setup (Custom)";
                                                const selected = agents.find(a => a.id === formData.voiceAgentId);
                                                return selected ? `${selected.internal_name} (${selected.role_type})` : "Select a role...";
                                            })()}
                                        </span>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="custom">Manual Setup (Custom)</SelectItem>
                                        {agents.map(agent => (
                                            <SelectItem key={agent.id} value={agent.id}>
                                                {agent.internal_name} ({agent.role_type})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="phoneNumber">Twilio Number</Label>
                                <Input
                                    id="phoneNumber"
                                    placeholder="+1-XXX-XXX-XXXX"
                                    value={formData.twilioPhoneNumber}
                                    onChange={(e) =>
                                        setFormData({ ...formData, twilioPhoneNumber: e.target.value })
                                    }
                                />
                            </div>

                            <div>
                                <Label htmlFor="firstMessage">First Message (Greeting)</Label>
                                {/* 🔴 PATCH шлёт только model; firstMessage лежит на верхнем
                                    уровне ассистента Vapi и этим путём в эфир не уезжает. */}
                                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                    Сохраняется только в базе OS; на звонки не влияет — меняется в панели Vapi.
                                </div>
                                <Textarea
                                    id="firstMessage"
                                    placeholder="Hello, thanks for calling our firm..."
                                    value={formData.firstMessage}
                                    onChange={(e) =>
                                        setFormData({ ...formData, firstMessage: e.target.value })
                                    }
                                    rows={3}
                                />
                            </div>

                            {/* [NEW] Enabled Languages Toggles */}
                            <div style={{ padding: '1rem', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.5rem' }}>
                                <Label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 600 }}>Enabled Languages</Label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                                            🇺🇸 <span>English</span>
                                            <span style={{ fontSize: '0.7rem', color: '#6b7280', marginLeft: '0.25rem' }}>(always on)</span>
                                        </span>
                                        <Switch
                                            checked={true}
                                            disabled={true}
                                            style={{ opacity: 0.6 }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                                            🇷🇺 <span>Russian</span>
                                        </span>
                                        <Switch
                                            checked={formData.enabledLanguages?.ru ?? true}
                                            onCheckedChange={(checked) =>
                                                setFormData({ ...formData, enabledLanguages: { ...formData.enabledLanguages, ru: checked } })
                                            }
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                                            🇪🇸 <span>Spanish</span>
                                        </span>
                                        <Switch
                                            checked={formData.enabledLanguages?.es ?? true}
                                            onCheckedChange={(checked) =>
                                                setFormData({ ...formData, enabledLanguages: { ...formData.enabledLanguages, es: checked } })
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* [NEW] Language Tabs */}
                            <div>
                                <Label>Language & Prompt</Label>
                                <Tabs value={activeLang} onValueChange={setActiveLang} style={{ marginTop: '0.5rem' }}>
                                    <TabsList className="grid grid-cols-1 sm:grid-cols-3 gap-1 p-1 w-full" style={{ height: 'auto', minHeight: '2.5rem' }}>
                                        <TabsTrigger value="en-US" className="py-2 text-xs sm:text-sm font-medium">🇺🇸 English (Default)</TabsTrigger>
                                        <TabsTrigger
                                            value="ru-RU"
                                            disabled={!(formData.enabledLanguages?.ru ?? true)}
                                            style={!(formData.enabledLanguages?.ru ?? true) ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                                            className="py-2 text-xs sm:text-sm font-medium"
                                        >
                                            🇷🇺 Russian
                                        </TabsTrigger>
                                        <TabsTrigger
                                            value="es-ES"
                                            disabled={!(formData.enabledLanguages?.es ?? true)}
                                            style={!(formData.enabledLanguages?.es ?? true) ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                                            className="py-2 text-xs sm:text-sm font-medium"
                                        >
                                            🇪🇸 Spanish
                                        </TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            </div>

                            {/* [260901-q2w] Состояние синхронизации с Vapi.
                                Длины показаны нарочно: разница «~4 500 против ~14 700»
                                должна быть видна глазом, а не выясняться расследованием. */}
                            <div style={{ padding: '0.75rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.5rem', fontSize: '0.8rem', color: '#334155' }}>
                                <div>
                                    Vapi: {isLinkedToVapi
                                        ? <code>{activeProfile.vapi_assistant_id}</code>
                                        : <strong>не привязан</strong>}
                                </div>
                                <div>
                                    Последняя синхронизация: {activeProfile && activeProfile.vapi_synced_at
                                        ? new Date(activeProfile.vapi_synced_at).toLocaleString()
                                        : 'никогда'}
                                </div>
                                <div>
                                    Длина текста в форме: {(formData.systemPromptTemplate || '').length} знаков
                                </div>
                                <div style={{ marginTop: '0.5rem' }}>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={!isLinkedToVapi || pulling}
                                        onClick={handlePullFromVapi}
                                    >
                                        {pulling ? 'Загружаю…' : 'Обновить из Vapi'}
                                    </Button>
                                </div>
                            </div>

                            {syncVerdict && (
                                <div
                                    style={{
                                        padding: '0.75rem',
                                        borderRadius: '0.5rem',
                                        fontSize: '0.8rem',
                                        border: '1px solid',
                                        borderColor: syncVerdict.tone === 'ok' ? '#86efac'
                                            : (syncVerdict.tone === 'warn' ? '#fde047' : '#fca5a5'),
                                        background: syncVerdict.tone === 'ok' ? '#f0fdf4'
                                            : (syncVerdict.tone === 'warn' ? '#fefce8' : '#fef2f2'),
                                        color: '#1f2937',
                                        whiteSpace: 'pre-wrap',
                                    }}
                                >
                                    {syncVerdict.text}
                                </div>
                            )}

                            <div>
                                <Label htmlFor="systemPrompt">System Prompt</Label>
                                {!isLinkedToVapi && (
                                    <div style={{ fontSize: '0.75rem', color: '#b45309', marginTop: '0.25rem' }}>
                                        ⚠️ Этот язык не привязан к ассистенту Vapi: правка НЕ повлияет на звонки.
                                    </div>
                                )}
                                <Textarea
                                    id="systemPrompt"
                                    placeholder={`System prompt for ${activeLang}...`}
                                    value={formData.systemPromptTemplate}
                                    onChange={(e) =>
                                        setFormData({ ...formData, systemPromptTemplate: e.target.value })
                                    }
                                    rows={15}
                                    style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                                />
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div>
                                    <Label htmlFor="isActive">Agent Active</Label>
                                </div>
                                <Switch
                                    id="isActive"
                                    checked={formData.isActive}
                                    onCheckedChange={(checked) =>
                                        setFormData({ ...formData, isActive: checked })
                                    }
                                />
                            </div>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="voice" style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <Card style={{ padding: '1.5rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '0.5rem' }}>
                                <Label style={{ marginBottom: '0.5rem', display: 'block' }}>Engine Provider</Label>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <Button
                                        type="button"
                                        variant={formData.provider === 'vapi' ? 'default' : 'outline'}
                                        onClick={() => setFormData({ ...formData, provider: 'vapi' })}
                                        style={{ flex: 1 }}
                                    >
                                        Vapi.ai (Legacy)
                                    </Button>
                                    <Button
                                        type="button"
                                        variant={formData.provider === 'internal' ? 'default' : 'outline'}
                                        onClick={() => setFormData({ ...formData, provider: 'internal' })}
                                        style={{ flex: 1 }}
                                    >
                                        MLG Voice Engine (Internal)
                                    </Button>
                                </div>
                            </div>

                            {formData.provider === 'vapi' ? (
                                <div>
                                    <Label htmlFor="voiceProvider">Vapi Voice Provider</Label>
                                    {/* 🔴 voice лежит на верхнем уровне ассистента Vapi:
                                        PATCH-ем model он не уезжает. */}
                                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                        Голос сохраняется только в базе OS; на звонки не влияет — меняется в панели Vapi.
                                    </div>
                                    <Select
                                        value={formData.voiceProvider}
                                        onValueChange={(value) =>
                                            setFormData({ ...formData, voiceProvider: value })
                                        }
                                    >
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                                            <SelectItem value="openai">OpenAI</SelectItem>
                                            <SelectItem value="azure">Azure</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <div style={{ marginTop: '1rem' }}>
                                        <Label htmlFor="voiceId">Voice ID</Label>
                                        <Input
                                            id="voiceId"
                                            value={formData.voiceId}
                                            onChange={(e) => setFormData({ ...formData, voiceId: e.target.value })}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                        <div>
                                            <Label>STT Provider</Label>
                                            <Select
                                                value={formData.sttProvider}
                                                onValueChange={(value) => setFormData({ ...formData, sttProvider: value })}
                                            >
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="deepgram">Deepgram</SelectItem>
                                                    <SelectItem value="google">Google</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label>TTS Provider</Label>
                                            <Select
                                                value={formData.ttsProvider}
                                                onValueChange={(value) => setFormData({ ...formData, ttsProvider: value })}
                                            >
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                                                    <SelectItem value="deepgram">Deepgram</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div>
                                        <Label>LLM Model</Label>
                                        <Select
                                            value={formData.llmModel}
                                            onValueChange={(value) => setFormData({ ...formData, llmModel: value })}
                                        >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                                                <SelectItem value="gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label htmlFor="voiceId">Voice ID</Label>
                                        <Input
                                            id="voiceId"
                                            value={formData.voiceId}
                                            onChange={(e) => setFormData({ ...formData, voiceId: e.target.value })}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* [deepgram-nova-3-upgrade] Model Selector */}
                            {formData.sttProvider === 'deepgram' && formData.provider === 'internal' && (
                                <div style={{ marginTop: '1rem', padding: '1rem', background: '#f0fdf4', borderRadius: '0.5rem', border: '1px solid #bbf7d0' }}>
                                    <Label>Deepgram Model Tier</Label>
                                    <Select
                                        value={formData.deepgramModel || 'nova-3'}
                                        onValueChange={(v) => setFormData({ ...formData, deepgramModel: v })}
                                    >
                                        <SelectTrigger style={{ backgroundColor: 'white' }}>
                                            <SelectValue placeholder="Select Model" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="nova-3">Nova-3 Multi (Runglish + Keyterms) 🚀</SelectItem>
                                            <SelectItem value="nova-2">Nova-2 (Legacy Fallback)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p style={{ fontSize: '0.75rem', color: '#166534', marginTop: '0.25rem' }}>
                                        Nova-3 supports real-time code-switching (RU/EN) and advanced keyterm prompting.
                                    </p>
                                </div>
                            )}
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="guardrails" style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <Card style={{ padding: '1.5rem' }}>
                        {/* Simplified Guardrails for brevity in this replace, ensuring core is there */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Label>Enable Tools</Label>
                                <Switch
                                    checked={formData.guardrails.enableTools}
                                    onCheckedChange={(c) => setFormData({ ...formData, guardrails: { ...formData.guardrails, enableTools: c } })}
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Label>Transfer on Angry</Label>
                                <Switch
                                    checked={formData.guardrails.transferOnAngry}
                                    onCheckedChange={(c) => setFormData({ ...formData, guardrails: { ...formData.guardrails, transferOnAngry: c } })}
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Label>Require Consent</Label>
                                <Switch
                                    checked={formData.guardrails.requireConsent}
                                    onCheckedChange={(c) => setFormData({ ...formData, guardrails: { ...formData.guardrails, requireConsent: c } })}
                                />
                            </div>
                            <div>
                                <Label>Max Duration</Label>
                                <Input type="number" value={formData.guardrails.maxCallDuration} onChange={(e) => setFormData({ ...formData, guardrails: { ...formData.guardrails, maxCallDuration: parseInt(e.target.value) } })} />
                            </div>
                        </div>
                    </Card>
                </TabsContent>
            </Tabs>

            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 mt-6">
                <Button
                    type="button"
                    variant="outline"
                    onClick={handleTestCall}
                    style={isFitenko ? { borderColor: '#E5E1D6', color: '#20231F' } : undefined}
                >
                    <Play size={16} style={{ marginRight: '0.5rem' }} /> Test Configuration
                </Button>
                <div className="flex items-center gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={refetchConfig}
                        style={isFitenko ? { borderColor: '#E5E1D6', color: '#687077' } : undefined}
                    >
                        <RefreshCw size={16} style={{ marginRight: '0.5rem' }} /> Reset
                    </Button>
                    <Button
                        type="submit"
                        disabled={loading || saving}
                        style={isFitenko ? {
                            backgroundColor: '#20231F',
                            color: '#FAF4E5',
                            border: '1px solid #C8A653',
                            fontWeight: '600'
                        } : undefined}
                    >
                        {saving ? (
                            <>Saving...</>
                        ) : (
                            <><Save size={16} style={{ marginRight: '0.5rem' }} /> Save Configuration</>
                        )}
                    </Button>
                </div>
            </div>
        </form>
    );
};

export default VoiceConfigPanel;
